import { memo, useCallback, useContext, useEffect, useRef, useState } from "react"
import ArticlesContext from "../context/ArticlesContext"
import Articles from "../layouts/Articles"
import { MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID, MODAL_MESSAGE_ERROR, MODAL_TYPE_ERROR, NUMBER_OF_ARTICLES_ON_PAGE } from "../HistoriografijaConstants"
import { useModalContext } from "../context/ModalContext"
import { useLocation, useNavigate } from "react-router-dom"
import { getAuthorByUrl } from "../services/AuthorService"
import { addCategoryNamesToArticles, loadArticles } from "../services/ArticleService"
import PaginationContext from "../context/PaginationContext"
import { setError, setIsNextButtonDisabled, setIsPreviousButtonDisabled, setReachedFirstPage } from "../context/ArticlesContextActions"


const AuthorArticles = memo(function AuthorArticles() {
    const { state, dispatch } = useContext(ArticlesContext)
    const {cursor, setCursor, direction, setDirection, lastUrlLocation, setLastUrlLocation} = useContext(PaginationContext)
    const { displayMessageModalWithParameters, resetModalContext, displayGenericErrorModal } = useModalContext()
    const navigate = useNavigate()
    const location = useLocation()
    const paginatedArticlesRef = useRef()

    const [pagination, setPagination] = useState({ paginatedArticles: [], totalNumber: 0 })
    const [filterValues, setFilterValues] = useState({
        authorName: "",
        authorUrl: "",
    })
    const [loading, setLoading] = useState(true)
    const [authorOrcid, setAuthorOrcid] = useState(null)

    const scrollToPaginatedArticles = useCallback(() => {
        if (paginatedArticlesRef.current) {
            paginatedArticlesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [paginatedArticlesRef])

    const handleException = useCallback((error) => {
        dispatch(setError(MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID))
        if (error.response.status === 404) {
            displayMessageModalWithParameters(MODAL_MESSAGE_ERROR, MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID, MODAL_TYPE_ERROR)
        } else displayGenericErrorModal()
        return
    }, [dispatch, displayMessageModalWithParameters, displayGenericErrorModal])

    const refreshAuthorArticles = useCallback(async (authorUrl) => {
        setCursor(0)
        setDirection(0)
        setLoading(true)
        if (state.categories && state.categories.length !== 0) {
            loadArticles(0, 0, null, null, null, authorUrl)
            .then(data => {
                setError(null)
                setLoading(false)
                setPagination({
                    paginatedArticles: addCategoryNamesToArticles(state.categories, data.content),
                    totalNumber: data.totalNumber
                })
                if (data.totalNumber <= NUMBER_OF_ARTICLES_ON_PAGE) {
                    dispatch(setIsNextButtonDisabled(true))
                } else {
                    dispatch(setIsNextButtonDisabled(false))
                }
                dispatch(setIsPreviousButtonDisabled(true))
                dispatch(setReachedFirstPage(false))
            })
            .catch(error => handleException(error))
        }
        
    }, [state.categories, handleException, setCursor, setDirection, dispatch, setLoading, setPagination])

    const loadNextAuthorArticles = useCallback(async (cursor, categoryId=null, subcategoryId=null, keyword=null, authorUrl) => {
        setCursor(cursor)
        setDirection(0)
        setLoading(true)
        if (state.categories && state.categories.length !== 0) {
            loadArticles(cursor, 0, null, null, null, authorUrl)
            .then(data => {
                dispatch(setError(null))
                setLoading(false)
                setPagination({
                    paginatedArticles: addCategoryNamesToArticles(state.categories, data.content),
                    totalNumber: data.totalNumber
                })
                if (data.totalNumber <= NUMBER_OF_ARTICLES_ON_PAGE) {
                    dispatch(setIsNextButtonDisabled(true))
                } else {
                    dispatch(setIsNextButtonDisabled(false))
                }
                setTimeout(() => scrollToPaginatedArticles(), 100)
            })
            .catch(error => handleException(error))
        }
    }, [state.categories, setCursor, setDirection, setLoading, dispatch, setPagination, scrollToPaginatedArticles, handleException])

    const loadPreviousAuthorArticles = useCallback(async (cursor, categoryId=null, subcategoryId=null, keyword=null, authorUrl) => {
        setCursor(cursor)
        setDirection(1)
        setLoading(true)
        if (state.categories && state.categories.length !== 0) {
            loadArticles(cursor, 1, null, null, null, authorUrl)
            .then(articlesResponse => {
                setLoading(false)
                setTimeout(() => scrollToPaginatedArticles(), 100)
                if (articlesResponse.totalNumber === NUMBER_OF_ARTICLES_ON_PAGE) {
                    dispatch(setIsPreviousButtonDisabled(true))
                    dispatch(setReachedFirstPage(true))
                    setPagination({
                        paginatedArticles: addCategoryNamesToArticles(state.categories, articlesResponse.content.reverse()),
                        totalNumber: articlesResponse.totalNumber
                    })
                    return
                } else if (articlesResponse.totalNumber < NUMBER_OF_ARTICLES_ON_PAGE) {
                    // Previous page should always have NUMBER_OF_ARTICLES_ON_PAGE or more articles
                    // If it has less, it means that an article has been added/deleted in the meantime while the user was going through the pages.
                    // We need to refresh the paginated articles if paginated articles are filtered
                    refreshAuthorArticles(authorUrl)
                    return
                }
                // There are more articles on the Previous page, so enable the button
                dispatch(setIsPreviousButtonDisabled(false))
                setPagination({
                    paginatedArticles: addCategoryNamesToArticles(state.categories, articlesResponse.content.reverse()),
                    totalNumber: articlesResponse.totalNumber
                })
            })
            .catch(error => handleException(error))
        }
    }, [state.categories, setCursor, setDirection, setLoading, scrollToPaginatedArticles, dispatch, setPagination, refreshAuthorArticles, handleException])

    useEffect(() => {
        setLoading(true)
        const currentUrlLocation = location.pathname
        const authorUrl = decodeURIComponent(currentUrlLocation).split('/').filter(segment => segment !== "")[1]
        // send API request for author information based on the authorUrl
        if (state.categories && state.categories.length !== 0) {
            getAuthorByUrl(authorUrl)
            .then(authorInfo => {
                setLoading(false)
                setFilterValues({
                    authorName: authorInfo.firstName + " " + authorInfo.lastName,
                    authorUrl: authorUrl
                })
                setAuthorOrcid(authorInfo.orcid)
                if (lastUrlLocation !== currentUrlLocation) {
                    // the url changed since the last render, reset pagination parameters
                    setLastUrlLocation(currentUrlLocation)
                    refreshAuthorArticles(authorUrl)
                } else {
                    direction === 0 ? loadNextAuthorArticles(cursor, null, null, null, authorUrl) : loadPreviousAuthorArticles(cursor, null, null, null, authorUrl)
                }
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    setLoading(false)
                    setError(MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID)
                    displayMessageModalWithParameters(MODAL_MESSAGE_ERROR, MODAL_DESCRIPTION_ERROR_ARTICLE_SEARCH_INVALID, MODAL_TYPE_ERROR)
                    // Redirect the user to home page after 3 seconds
                    setTimeout(() => {
                        navigate('/')
                        resetModalContext()
                    }, 3000)
                } else displayGenericErrorModal()
            })
        }
        
    }, [location.pathname, refreshAuthorArticles, loadNextAuthorArticles, loadPreviousAuthorArticles])

    return(
        <div className="flex-container flex-column flex-grow-1">
            <div className="flex-container flex-column flex-grow-1 full-width center-align">
                <Articles ref={paginatedArticlesRef} filterValues={filterValues} paginatedArticles={pagination.paginatedArticles}
                 totalNumber={pagination.totalNumber} reloadFunction={refreshAuthorArticles} loadNextArticles={loadNextAuthorArticles}
                 loadPreviousArticles={loadPreviousAuthorArticles} loading={loading} authorOrcid={authorOrcid}/>
            </div>
        </div>
    )
})

export default AuthorArticles